import { supabase } from "@/integrations/supabase/client";
import { fetchCurrentCompanyId } from "@/hooks/useCurrentCompany";
import type { QzApi } from "./qzTray.types";
import { coerceQzPlainTextResponse } from "./qzSigningResponse";
import {
  NoLabelPrinterError,
  pickAutoLabelPrinter,
  resolvePrinterChoice,
} from "./printerSelection";
import {
  getWorkstationLabelPrintingSettings,
  setWorkstationLabelPrintingSettings,
} from "./workstationSettings";

const QZ_SCRIPT_SRC = "/js/qz-tray.js";

let qzLoadPromise: Promise<QzApi> | null = null;
let qzConnectPromise: Promise<void> | null = null;
let securityConfigured = false;

function getQz(): QzApi {
  if (!window.qz) {
    throw new Error("QZ Tray library is not loaded.");
  }
  return window.qz;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      // Script tag present — wait for load if qz isn't ready yet.
      if (window.qz) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)));
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Calls the `qz-signing` edge function.
 * Contract: response body is plain text (PEM / base64 signature), never JSON.
 */
async function fetchQzSigning(
  action: "certificate" | "sign",
  requestToSign?: string,
): Promise<string> {
  const companyId = await fetchCurrentCompanyId();
  if (!companyId) {
    throw new Error("No active company selected.");
  }

  const { data, error } = await supabase.functions.invoke("qz-signing", {
    body: {
      action,
      company_id: companyId,
      ...(requestToSign ? { request: requestToSign } : {}),
    },
    headers: { Accept: "text/plain" },
  });

  if (error) {
    const context = (error as { context?: Response }).context;
    if (context) {
      const text = await context.clone().text().catch(() => "");
      if (text.trim()) throw new Error(text.trim());
    }
    throw new Error(error.message || "QZ signing failed");
  }

  return coerceQzPlainTextResponse(data);
}

function configureQzSecurity(qz: QzApi): void {
  if (securityConfigured) return;

  qz.security.setCertificatePromise((resolve, reject) => {
    fetchQzSigning("certificate").then(resolve).catch(reject);
  });

  qz.security.setSignatureAlgorithm("SHA512");
  qz.security.setSignaturePromise(
    (toSign) => (resolve, reject) => {
      fetchQzSigning("sign", toSign).then(resolve).catch(reject);
    },
  );

  securityConfigured = true;
}

export async function loadQzTray(): Promise<QzApi> {
  if (typeof window === "undefined") {
    throw new Error("QZ Tray not available in this environment");
  }
  if (window.qz) {
    configureQzSecurity(window.qz);
    return window.qz;
  }
  if (!qzLoadPromise) {
    qzLoadPromise = loadScript(QZ_SCRIPT_SRC)
      .then(() => {
        const qz = getQz();
        configureQzSecurity(qz);
        return qz;
      })
      .catch((err) => {
        qzLoadPromise = null;
        throw err;
      });
  }
  return qzLoadPromise;
}

export async function isQzTrayAvailable(): Promise<boolean> {
  try {
    await ensureQzConnected();
    return true;
  } catch {
    return false;
  }
}

export async function ensureQzConnected(): Promise<QzApi> {
  const qz = await loadQzTray();
  // QZ's isActive() includes CONNECTING, before sendData is installed.
  // All probes, discovery and prints must await the same handshake.
  if (qzConnectPromise) {
    await qzConnectPromise;
  } else if (!qz.websocket.isActive()) {
    const connection = qz.websocket.connect();
    qzConnectPromise = connection;
    try {
      await connection;
    } finally {
      qzConnectPromise = null;
    }
  }
  return qz;
}

/** @deprecated Use `pickAutoLabelPrinter`; kept for compatibility, never falls back to a non-Zebra printer. */
export function pickDefaultZebraPrinter(printers: string[], preferred?: string | null): string | null {
  if (preferred && printers.includes(preferred)) return preferred;
  return pickAutoLabelPrinter(printers);
}

export function getStoredZebraPrinter(): string | null {
  return getWorkstationLabelPrintingSettings().printerName;
}

/** Store a printer the user chose by hand. */
export function storeZebraPrinter(name: string): void {
  setWorkstationLabelPrintingSettings({ printerName: name, printerSource: "manual" });
}

/** Resolve the printer to use: manual choice, else an auto-picked Zebra, else throw. */
export async function resolveZebraPrinter(): Promise<string> {
  const ws = getWorkstationLabelPrintingSettings();
  if (ws.printerName && ws.printerSource === "manual") return ws.printerName;
  const qz = await ensureQzConnected();
  const printers = await qz.printers.find();
  const choice = resolvePrinterChoice({
    printers,
    stored: ws.printerName,
    storedSource: ws.printerSource,
  });
  if (!choice.printer) throw new NoLabelPrinterError();
  setWorkstationLabelPrintingSettings({ printerName: choice.printer, printerSource: "auto" });
  return choice.printer;
}

export async function listQzPrinters(): Promise<string[]> {
  const qz = await ensureQzConnected();
  return qz.printers.find();
}

/** Print a raw ZPL payload to the given printer, or the resolved one. Never changes the stored choice. */
export async function printRawZpl(zpl: string, printerName?: string): Promise<string> {
  const qz = await ensureQzConnected();
  const printer = printerName ?? (await resolveZebraPrinter());
  const config = qz.configs.create(printer, { forceRaw: true });
  await qz.print(config, [{ type: "raw", format: "plain", data: zpl }]);
  return printer;
}

export function qzErrorMessage(err: unknown): string {
  if (err instanceof NoLabelPrinterError) return err.message;
  const message = err instanceof Error ? err.message : String(err);
  if (/sendData is not a function|connection is not established/i.test(message)) {
    return "QZ Tray is not ready. Install and start QZ Tray, then try again.";
  }
  if (/websocket|connect/i.test(message)) {
    return "Could not connect to QZ Tray. Install and start QZ Tray on this computer.";
  }
  if (/QZ (certificate|private key) not configured/i.test(message)) {
    return "QZ signing keys are not configured. Ask an admin to add them under Company Settings → Label printing.";
  }
  return message;
}
